/**
 * member-merge-page.tsx — Admin-portal page for merging duplicate member records.
 *
 * Route: /member-merge
 * Required roles: staff:admin OR staff:wa (enforced by RequireAuth in app.tsx).
 *
 * Flow:
 * 1. Staff searches for the retained member (record to keep)
 * 2. Staff searches for the deprecated member (record to deprecate)
 * 3. Staff confirms the merge via MemberMergeForm
 * 4. System calls POST /v1/members/merge (betty-core-api)
 * 5. Audit log entry written (no PHI — memberIds + staffId only)
 *
 * PHI constraint: This page displays only internal ULIDs, status, and program count.
 * No member names, DOBs, or external IDs are rendered.
 */

import { useState } from 'react';
import type { JSX } from 'react';

import { MemberMergeForm } from './member-merge-form.js';
import { MemberSearch } from './member-search.js';

export function MemberMergePage(): JSX.Element {
  const [retainedMemberId, setRetainedMemberId] = useState<string | undefined>(undefined);
  const [deprecatedMemberId, setDeprecatedMemberId] = useState<string | undefined>(undefined);
  const [showForm, setShowForm] = useState(false);

  function handleProceed(): void {
    if (retainedMemberId !== undefined && deprecatedMemberId !== undefined) {
      setShowForm(true);
    }
  }

  function handleComplete(): void {
    // Reset form state after successful merge
    setRetainedMemberId(undefined);
    setDeprecatedMemberId(undefined);
    setShowForm(false);
  }

  const canProceed =
    retainedMemberId !== undefined &&
    deprecatedMemberId !== undefined &&
    retainedMemberId !== deprecatedMemberId;

  return (
    <div className="p-8">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">Member Record Merge</h1>
        <p className="mt-2 text-gray-500">
          Merge two duplicate member records into one. This action is irreversible. Select the
          record to retain and the record to deprecate.
        </p>
      </div>

      {!showForm ? (
        <div className="space-y-8">
          <div className="rounded-lg border border-gray-200 bg-white p-6">
            <MemberSearch
              label="Step 1: Select the record to RETAIN (all history will be kept here)"
              onSelect={setRetainedMemberId}
              selectedMemberId={retainedMemberId}
            />
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-6">
            <MemberSearch
              label="Step 2: Select the record to DEPRECATE (will be marked inactive)"
              onSelect={setDeprecatedMemberId}
              selectedMemberId={deprecatedMemberId}
            />
          </div>

          {retainedMemberId !== undefined &&
            deprecatedMemberId !== undefined &&
            retainedMemberId === deprecatedMemberId && (
              <p className="text-sm text-red-600" role="alert">
                Retained and deprecated records must be different.
              </p>
            )}

          <div className="flex justify-end">
            <button
              type="button"
              disabled={!canProceed}
              onClick={handleProceed}
              className="rounded-md bg-blue-600 px-6 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Proceed to confirm
            </button>
          </div>
        </div>
      ) : (
        retainedMemberId !== undefined &&
        deprecatedMemberId !== undefined && (
          <MemberMergeForm
            retainedMemberId={retainedMemberId}
            deprecatedMemberId={deprecatedMemberId}
            onComplete={handleComplete}
          />
        )
      )}
    </div>
  );
}
